import { useEffect, useRef } from "preact/hooks";
import type { CoreData } from "../data/types";
import { useStore } from "../state/store";
import { decisions, holdToast, settings, ui } from "../state/app";
import { entryFor } from "../data/load";
import { partnerName, partnerSets } from "../state/partner";
import { goTab, route } from "../state/router";
import { genderClass, initialsOf } from "../data/format";
import { confetti } from "./confetti";
import { IconHeart } from "./icons";
import { isTopModal, pushModal, restoreFocus, trapTab } from "./modalStack";
import { maybeAskForReview } from "../state/review";

export function MatchModal({ core }: { core: CoreData }) {
  const key = useStore(ui, (s) => s.match);
  const myName = useStore(settings, (s) => s.myName);
  // "love" only when you both loved it; their love is still mentioned when you only liked it
  const theyLove = useStore(partnerSets, (s) => (key ? s.loved.has(key) : false));
  const iLove = useStore(decisions, (s) => (key ? s.map[key]?.d === 2 : false));
  const btn = useRef<HTMLButtonElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const saved = useStore(decisions, (s) => (key ? s.map[key]?.n : undefined));
  const e = key ? entryFor(core, key, saved) : undefined;

  useEffect(() => {
    const box = root.current;
    if (!e || !box) return;
    const opener = document.activeElement;
    const pop = pushModal(box);
    const css = getComputedStyle(document.documentElement);
    const c = (v: string) => css.getPropertyValue(v).trim() || "#e2603f";
    // behind the card, so the name stays readable
    confetti([c("--like"), c("--love"), c("--girl"), c("--boy"), c("--unisex")], { into: box });
    window.setTimeout(() => btn.current?.focus(), 50);
    const onKey = (ev: KeyboardEvent) => {
      if (!isTopModal(box)) return;
      if (ev.key === "Escape") {
        ev.stopPropagation();
        close();
      } else trapTab(ev, box);
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      pop();
      restoreFocus(opener);
    };
  }, [key]);

  if (!e) return null;
  const close = () => {
    ui.set({ match: null });
    // agreeing on a name is the happiest moment to ask for a rating (if it's time: review.ts)
    maybeAskForReview();
  };
  const them = partnerName();

  return (
    <div
      class="match-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="match-title match-name"
      aria-describedby="match-sub"
      onClick={close}
      ref={root}
    >
      <div class={`match-card g-${genderClass(e)}`} onClick={(ev) => ev.stopPropagation()}>
        <p class="match-kicker" id="match-title">
          You both {theyLove && iLove ? "love" : "like"}
        </p>
        <div class="match-avatars" aria-hidden="true">
          <span class="avatar">{initialsOf(myName) || "You"}</span>
          <span class="match-heart">
            <IconHeart size={22} filled />
          </span>
          <span class="avatar avatar-2">{initialsOf(them) || "❤"}</span>
        </div>
        <h2 class="match-name" id="match-name">
          {e.name}
        </h2>
        <p class="match-sub" id="match-sub">
          {them} {theyLove ? (iLove ? "loves it too" : "loves it") : "likes it too"}. It's in your matches.
        </p>
        <div class="match-actions">
          <button class="btn btn-primary btn-wide" ref={btn} onClick={close}>
            Keep swiping
          </button>
          <button
            class="btn btn-ghost btn-wide"
            onClick={() => {
              close();
              goTab("together");
            }}
          >
            See all matches
          </button>
        </div>
      </div>
    </div>
  );
}

export function Toast() {
  const t = useStore(ui, (s) => s.toast);
  // on the deck, messages sit above the swipe buttons so they never cover them
  const onDeck = useStore(route, (r) => r.tab === "swipe" && !r.overlay);
  if (!t) return <div class="toast-wrap" aria-live="polite" />;
  return (
    <div class={`toast-wrap ${onDeck ? "above-deck" : ""}`} aria-live={t.spoken ? "off" : "polite"}>
      <div class="toast" key={t.id} onFocusIn={() => holdToast(true)} onFocusOut={() => holdToast(false)}>
        <span>{t.text}</span>
        {t.action && (
          <button
            class="toast-action"
            onClick={() => {
              t.action!.run();
              ui.set({ toast: null });
            }}
          >
            {t.action.label}
          </button>
        )}
      </div>
    </div>
  );
}
